/**
 * The Welcome questionnaire (F-25): what a new customer is here to make and which AI tools they already
 * have. The answers only choose what Welcome shows first (a recommended product, the key groups that
 * matter, whether the subscriptions step is relevant); every option stays available in Settings.
 *
 * They are a per-browser convenience, kept in localStorage per organization. Nothing here is billing,
 * entitlement or permission state, and a missing or unreadable entry just means "not answered yet".
 */

/** Capability ids shared with the provider-key groups (`components/Settings/providerCapabilities.ts`). */
export type WelcomeCapability = 'writing' | 'images' | 'video' | 'voice' | 'avatars' | 'vision';

export interface WelcomeGoal {
  id: string;
  label: string;
  needs: WelcomeCapability[];
  /** Catalog entry ids that fit this goal, best first. */
  products: string[];
}

export const WELCOME_GOALS: readonly WelcomeGoal[] = [
  { id: 'book', label: 'A book or novel', needs: ['writing'], products: ['PROD-STORY'] },
  { id: 'screenplay', label: 'A screenplay, script or shot list', needs: ['writing'], products: ['PROD-PREP'] },
  { id: 'film', label: 'A film or video', needs: ['writing', 'images', 'video', 'voice'], products: ['PROD-FILM'] },
  { id: 'audiobook', label: 'An audiobook or podcast', needs: ['writing', 'voice'], products: ['PROD-STORY'] },
  { id: 'course', label: 'A course or lessons', needs: ['writing', 'voice', 'avatars'], products: ['PROD-LEARNING'] },
  { id: 'game', label: 'A game', needs: ['writing', 'images'], products: ['PROD-GAME-FORGE'] },
  { id: 'launch', label: 'A launch or marketing campaign', needs: ['writing', 'images', 'video'], products: ['PROD-LAUNCH'] },
  { id: 'edit', label: 'Editing footage in DaVinci Resolve', needs: ['writing'], products: ['PROD-AUTEUREDIT'] },
  { id: 'other', label: 'Something else', needs: ['writing'], products: [] },
];

export interface WelcomeTool {
  id: string;
  label: string;
  covers: WelcomeCapability[];
  /** A consumer subscription (ChatGPT Plus, Claude Pro…) that the desktop app will be able to use. */
  subscription?: boolean;
}

export const WELCOME_TOOLS: readonly WelcomeTool[] = [
  { id: 'chatgpt', label: 'ChatGPT or an OpenAI account', covers: ['writing', 'images'], subscription: true },
  { id: 'claude', label: 'Claude (Anthropic)', covers: ['writing'], subscription: true },
  { id: 'gemini', label: 'Google Gemini', covers: ['vision'], subscription: true },
  { id: 'grok', label: 'Grok', covers: [], subscription: true },
  { id: 'openrouter', label: 'OpenRouter', covers: ['writing'] },
  { id: 'elevenlabs', label: 'ElevenLabs (voices)', covers: ['voice'] },
  { id: 'video', label: 'A video generator (Kling, Veo, Seedance, Runway…)', covers: ['video'] },
  { id: 'heygen', label: 'HeyGen (avatars)', covers: ['avatars'] },
  { id: 'images', label: 'An image generator', covers: ['images'] },
  { id: 'none', label: 'None yet', covers: [] },
];

/** One plain sentence per capability a goal needs and no listed tool covers. */
export const MISSING_ADVICE: Record<WelcomeCapability, string> = {
  writing: 'For writing you need a text AI key. The easiest start is OpenRouter: one free account and one key that reaches many models (OpenAI or Anthropic keys work too).',
  voice: 'For narration and voices you need a voice provider, such as ElevenLabs.',
  video: 'For video you need a video provider. Auteur reaches Kling, Veo and Seedance through one WaveSpeed key.',
  images: 'For images you need an image provider. An OpenAI key covers image generation.',
  avatars: 'For a talking presenter you need a HeyGen key.',
  vision: 'For image analysis you can use a Google Gemini key.',
};

export interface WelcomeAnswers {
  goals: string[];
  tools: string[];
  /** Set once the customer has chosen on the product step: bought, or decided later. */
  plan: 'checkout' | 'later' | null;
}

const STORAGE_PREFIX = 'auteur.welcome.v1.';

function known(values: unknown, catalog: readonly { id: string }[]): string[] {
  if (!Array.isArray(values)) return [];
  const ids = new Set(catalog.map((item) => item.id));
  return values.filter((value): value is string => typeof value === 'string' && ids.has(value));
}

export function readWelcomeAnswers(orgId: string): WelcomeAnswers | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + orgId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      goals: known(parsed.goals, WELCOME_GOALS),
      tools: known(parsed.tools, WELCOME_TOOLS),
      plan: parsed.plan === 'checkout' || parsed.plan === 'later' ? parsed.plan : null,
    };
  } catch {
    return null;
  }
}

export function saveWelcomeAnswers(orgId: string, answers: WelcomeAnswers): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + orgId, JSON.stringify(answers));
  } catch {
    // Storage may be unavailable (private window); Welcome then simply asks again next time.
  }
}

/** Every capability the chosen goals need, in a stable order. */
export function neededCapabilities(goals: string[]): WelcomeCapability[] {
  const order: WelcomeCapability[] = ['writing', 'voice', 'images', 'video', 'avatars', 'vision'];
  const needed = new Set(WELCOME_GOALS.filter((goal) => goals.includes(goal.id)).flatMap((goal) => goal.needs));
  return order.filter((capability) => needed.has(capability));
}

/** Needed capabilities that none of the listed tools covers. */
export function missingCapabilities(goals: string[], tools: string[]): WelcomeCapability[] {
  const covered = new Set(WELCOME_TOOLS.filter((tool) => tools.includes(tool.id)).flatMap((tool) => tool.covers));
  return neededCapabilities(goals).filter((capability) => !covered.has(capability));
}

/** Catalog ids that fit the goals, best first, without repeats. */
export function recommendedProducts(goals: string[]): string[] {
  const seen = new Set<string>();
  for (const goal of WELCOME_GOALS) {
    if (!goals.includes(goal.id)) continue;
    for (const product of goal.products) seen.add(product);
  }
  return [...seen];
}

/** True when the customer named a consumer subscription the desktop app could use. */
export function subscriptionsRelevant(tools: string[]): boolean {
  return WELCOME_TOOLS.some((tool) => tool.subscription && tools.includes(tool.id));
}
